import {
  ALGOLIA_FAMILY_CODE_AUDIO,
  ALGOLIA_FAMILY_CODE_CONSOLE,
  ALGOLIA_FAMILY_CODE_MACBOOK,
  ALGOLIA_FAMILY_CODE_SMARTPHONE,
  ALGOLIA_OTHER_FAMILIES,
  type AlgoliaHit,
  type AttributesType,
  type FamilyCode,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-products.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

export const ALGOLIA_DATE_KEY = 'co_release_date'
const ALGOLIA_BRAND_KEY = 'bb_brand'
const ALGOLIA_STORAGE_KEY = 'bb_storage'
const ALGOLIA_MODEL_KEY = 'bb_model'
const ALGOLIA_CARRIER_KEY = 'co_carrier'
const ALGOLIA_SCREEN_KEY = 'bb_screen_size'
const ALGOLIA_YEAR_KEY = 'bb_release_year'
const ALGOLIA_PROCESSOR_KEY = 'bb_processor_type_speed'
const ALGOLIA_GRAPHIC_CARD_KEY = 'bb_graphic_card'
const ALGOLIA_MEMORY_KEY = 'bb_memory'
const ALGOLIA_KEYBOARD_KEY = 'bb_keyboard_type'
const ALGOLIA_PRODUCT_LINE_KEY = 'bb_product_line'
const ALGOLIA_AUDIO_ACCESSORY_KEY = 'bb_audio_accessory'
const ALGOLIA_CONSOLE_ACCESSORY_KEY = 'bb_console_accessory'

const ATTRIBUTES_HYPHEN_SEPARATOR = ' - '
const ATTRIBUTES_SPACE_SEPARATOR = ' '

const SHORT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
}

type AttributeEntry = [string, string | number]
type AttributeFormatter = (entries: AttributeEntry) => string | null

type StrategyItem = { id: string; formatter: AttributeFormatter | null }
type Strategy = Array<StrategyItem | StrategyItem[]>

const buildMemoryUnitLabel: AttributeFormatter = (entries) => {
  const [key, value] = entries

  const start = key.indexOf('(')
  if (start >= 0) {
    const end = key.indexOf(')', start + 1)
    if (end >= 0) {
      return `${value}${key.substring(start + 1, end)}`
    }
  }

  return null
}

export type ProductAlgoliaHitAttribute = {
  id: string
  label: string
  value: string | number
}

export type ProductAlgoliaHit = {
  id: string
  title: string
  image: string | null
  attributes: ProductAlgoliaHitAttribute[]
}

const formatAttribute = (
  attribute: {
    id: string
    formatter: AttributeFormatter | null
  },
  attributes: AttributesType,
) => {
  const { id, formatter } = attribute

  if (!attributes[id]) {
    return null
  }

  const labelValuePair = attributes[id]
  const [entries] = Object.entries(labelValuePair)

  return (formatter && formatter(entries)) || entries[1]
}

const removeEmptyAttributes = (attribute: unknown): boolean =>
  !isEmpty(attribute)

const buildProductTitle = ({
  strategy,
  attributes,
}: {
  strategy: Strategy
  attributes: AttributesType
}) => {
  return strategy
    .map((item) => {
      if (Array.isArray(item)) {
        const composedAttrsLabel = item
          .map((attribute) => {
            return formatAttribute(attribute, attributes)
          })
          .filter(removeEmptyAttributes)
          .join(ATTRIBUTES_SPACE_SEPARATOR)

        return composedAttrsLabel
      }

      return formatAttribute(item, attributes)
    })
    .filter(removeEmptyAttributes)
    .join(ATTRIBUTES_HYPHEN_SEPARATOR)
}

export const getProductTitle = ({
  familyCode,
  attributes = {},
}: {
  familyCode: string
  attributes?: AttributesType
}) => {
  const strategies = {
    [ALGOLIA_FAMILY_CODE_MACBOOK]: [
      [
        { id: ALGOLIA_MODEL_KEY, formatter: null },
        {
          id: ALGOLIA_SCREEN_KEY,
          formatter: (entries: AttributeEntry) => `${entries[1]}″`,
        },
        {
          id: ALGOLIA_YEAR_KEY,
          formatter: (entries: AttributeEntry) => `(${entries[1]})`,
        },
      ],
      { id: ALGOLIA_PROCESSOR_KEY, formatter: null },
      { id: ALGOLIA_GRAPHIC_CARD_KEY, formatter: null },
      {
        id: ALGOLIA_STORAGE_KEY,
        formatter: (entries: AttributeEntry) => buildMemoryUnitLabel(entries),
      },
      {
        id: ALGOLIA_MEMORY_KEY,
        formatter: (entries: AttributeEntry) => buildMemoryUnitLabel(entries),
      },
      { id: ALGOLIA_KEYBOARD_KEY, formatter: null },
    ],
    [ALGOLIA_FAMILY_CODE_SMARTPHONE]: [
      { id: ALGOLIA_BRAND_KEY, formatter: null },
      { id: ALGOLIA_MODEL_KEY, formatter: null },
      {
        id: ALGOLIA_STORAGE_KEY,
        formatter: (entries: AttributeEntry) => buildMemoryUnitLabel(entries),
      },
      { id: ALGOLIA_CARRIER_KEY, formatter: null },
    ],
    [ALGOLIA_FAMILY_CODE_AUDIO]: [
      [
        { id: ALGOLIA_BRAND_KEY, formatter: null },
        { id: ALGOLIA_PRODUCT_LINE_KEY, formatter: null },
      ],
      { id: ALGOLIA_AUDIO_ACCESSORY_KEY, formatter: null },
      {
        id: ALGOLIA_STORAGE_KEY,
        formatter: (entries: AttributeEntry) => buildMemoryUnitLabel(entries),
      },
    ],
    [ALGOLIA_FAMILY_CODE_CONSOLE]: [
      [
        { id: ALGOLIA_BRAND_KEY, formatter: null },
        { id: ALGOLIA_PRODUCT_LINE_KEY, formatter: null },
      ],
      { id: ALGOLIA_CONSOLE_ACCESSORY_KEY, formatter: null },
      {
        id: ALGOLIA_STORAGE_KEY,
        formatter: (entries: AttributeEntry) => buildMemoryUnitLabel(entries),
      },
    ],
    [ALGOLIA_OTHER_FAMILIES]: [
      { id: ALGOLIA_BRAND_KEY, formatter: null },
      { id: ALGOLIA_MODEL_KEY, formatter: null },
      {
        id: ALGOLIA_STORAGE_KEY,
        formatter: (entries: AttributeEntry) => buildMemoryUnitLabel(entries),
      },
    ],
  }
  const strategy =
    strategies[familyCode as FamilyCode] || strategies[ALGOLIA_OTHER_FAMILIES]

  return buildProductTitle({ strategy, attributes })
}

export const formatAttributes = (attributes: AttributesType) => {
  const i18n = useI18n()

  return Object.entries(attributes).map((attribute) => {
    const [id, attrPair] = attribute
    const [label, value] = Object.entries(attrPair)[0]

    const attributeDetails = {
      id,
      label,
      value:
        id === ALGOLIA_DATE_KEY
          ? i18n.date(new Date(value), SHORT_DATE_FORMAT)
          : value,
    }

    return attributeDetails
  })
}

export const productAlgoliaHitsAdapter = (
  hits: AlgoliaHit[],
): ProductAlgoliaHit[] => {
  return hits.map((hit) => {
    return {
      id: hit.uuid,
      title: getProductTitle({
        familyCode: hit.familyCode,
        attributes: hit.attributes,
      }),
      image: hit.image,
      attributes: formatAttributes(hit.attributes),
    }
  })
}
